import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { PageIndexation } from '@app/core/content/models';
import { LinkService } from '@app/shared/services/link/link.service';
import { UrlGeneratorService, mapTransKeyToLocale } from '@app/core/i18n';
import { ContentsLocalizedPath } from '@vestiaire/api-sdk-angular';
import { DOCUMENT } from '@angular/common';
import { environment } from '@env/environment';

@Injectable()
export class PageService {
  private readonly hreflang = {
    'fr-FR': 'fr',
    'en-GB': 'en-gb',
    'en-US': 'en-us',
    'de-DE': 'de',
    'it-IT': 'it',
    'es-ES': 'es'
  };

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private linkService: LinkService,
    private urlGenerator: UrlGeneratorService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setDescription(content: string): void {
    this.metaService.updateTag({ name: 'description', content });
  }

  setKeywords(content: string): void {
    this.metaService.updateTag({ name: 'keywords', content });
  }

  removeKeywords(): void {
    this.metaService.removeTag('name=keywords');
  }

  setLanguage(lang: string): void {
    const hreflang = lang in this.hreflang ? this.hreflang[lang] : lang;
    this.document.documentElement.setAttribute('lang', hreflang);
  }

  /**
   * Sets canonical version of page.
   * @param path canonical path
   */
  setCanonicalVersion(path: string): void {
    const href = this.urlGenerator.generateUrl(path);
    const attributes = { rel: 'canonical', href };
    this.linkService.updateTag(attributes, 'rel=canonical');
  }

  /**
   * Sets a language version of the page.
   *
   * The value of the `lang` property identifies the language (in ISO 639-1 format)
   * and optionally the region (in ISO 3166-1 Alpha 2 format) of alternate page.
   *
   * For example:
   *  - `be`: Belarusian language, independent of region (**not** Belgium French).
   *  - `nl-be`: Dutch for Belgium.
   *  - `fr-be`: French for Belgium.
   *
   * @param path canonical path of alternate version
   * @param lang language of the alternate version
   */
  setLanguageVersion(path: string, lang: string): void {
    const hreflang = lang in this.hreflang ? this.hreflang[lang] : lang;
    const href = this.urlGenerator.generateUrl(path, lang === 'x-default' ? 'en-GB' : lang);
    this.linkService.addTag({ rel: 'alternate', hreflang, href }, true, true);
  }

  clearLanguageVersions(): void {
    this.linkService.removeTag('rel=alternate');
  }

  /**
   * Sets language versions of the page.
   *
   * @param versions Alternate version as key-value pairs.
   */
  setLanguageVersions(versions: { [lang: string]: string }): void {
    this.clearLanguageVersions();

    Object.keys(versions).forEach(lang => {
      this.setLanguageVersion(versions[lang], lang);
    });
  }

  /**
   * Sets language versions of the page with a ContentsLocalizedPath array
   *
   * @param content ContentsLocalizedPath[]
   */
  setLanguageVersionsWithContentsLocalizedPath(content: ContentsLocalizedPath[]): void {
    this.clearLanguageVersions();
    for (const version of content) {
      this.setLanguageVersion(version.path, mapTransKeyToLocale(version.language) || version.language);
    }
  }

  setRobots(robots: PageIndexation): void {
    const directives = new Array();
    directives.push(robots.index ? 'index' : 'noindex');
    directives.push(robots.follow ? 'follow' : 'nofollow');
    this.metaService.updateTag({ name: 'robots', content: directives.join(', ') });
  }

  getTag(tagName: string): string {
    return this.metaService.getTag('name=' + tagName).content;
  }

  setPaginationLink(sRel: string, sHref: string): void {
    if (!sHref || sHref.length === 0) {
      this.linkService.removeTag('rel=' + sRel);
    } else {
      this.linkService.addTag({ rel: sRel, href: sHref });
    }
  }

  initAlternateLinks(path: string): void {
    const hostnames = environment.i18n.hosts;
    this.clearLanguageVersions();
    for (const locale of Object.keys(hostnames)) {
      this.setLanguageVersion(path, locale);
    }
  }
}
